import React, {useId, useRef, useState} from 'react';
import {Link} from 'react-router-dom';
import './Contact.css';
import company from '../config/company';
import kontaktBild from '../assets/images/ueberUns3-800.webp';
import {submitContactMessage} from '../firebase';

const initial = {name: '', email: '', phone: '', reachability: '', message: '', consent: false, honey: ''};

/**
 * Kontaktformular. Die Anfrage wird in Firestore gespeichert
 * und per Cloud Function als E-Mail an das Büro weitergeleitet.
 */
export default function Contact({
  title = 'Jetzt Kontakt aufnehmen',
  hint = 'Bitte senden Sie über dieses Formular keine medizinischen Unterlagen oder sensiblen Gesundheitsdaten.'
}) {
  const id = useId();
  const [form, setForm] = useState(initial);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [submitError, setSubmitError] = useState('');
  // Zeitpunkt, ab dem das Formular sichtbar war (Spam-Schutz: Mindest-Ausfüllzeit).
  const startedAt = useRef(Date.now());

  const onChange = (e) => {
    const {name, value, type, checked} = e.target;
    setForm((s) => ({...s, [name]: type === 'checkbox' ? checked : value}));
  };

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = 'Bitte Ihren Namen eingeben.';
    if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) e.email = 'Bitte eine gültige E-Mail-Adresse angeben.';
    if (form.phone.trim() && !/^[\d+()\-.\s/]{6,}$/.test(form.phone.trim())) e.phone = 'Bitte eine gültige Telefonnummer angeben.';
    if (!form.consent) e.consent = 'Bitte bestätigen Sie die Datenschutzerklärung.';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    if (!validate()) return;

    try {
      setLoading(true);
      await submitContactMessage({
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        reachability: form.reachability.trim(),
        message: form.message.trim(),
        consent: form.consent,
        honey: form.honey,
        startedAt: startedAt.current
      });
      setDone(true);
      setForm(initial);
      setErrors({});
    } catch (err) {
      console.error(err);
      setSubmitError(
        err.status === 429
          ? `Es sind gerade sehr viele Anfragen eingegangen. Bitte rufen Sie uns direkt an: ${company.phone}.`
          : `Das Senden hat leider nicht geklappt. Bitte versuchen Sie es später erneut oder rufen Sie uns an: ${company.phone}.`
      );
    } finally {
      setLoading(false);
    }
  };

  const field = (name, label, props = {}, required = true) => (
    <div className={`contact-field ${errors[name] ? 'has-error' : ''}`}>
      <label htmlFor={`${id}-${name}`}>
        {label} {required ? <span aria-hidden="true">*</span> : <span className="contact-optional">(optional)</span>}
      </label>
      <input
        id={`${id}-${name}`}
        name={name}
        value={form[name]}
        onChange={onChange}
        required={required}
        aria-invalid={errors[name] ? 'true' : undefined}
        aria-describedby={errors[name] ? `${id}-${name}-error` : undefined}
        {...props}
      />
      {errors[name] && <small id={`${id}-${name}-error`}>{errors[name]}</small>}
    </div>
  );

  return (
    <section className="contact" id="kontakt">
      <div className="contact-inner">
        <div className="contact-media">
          <img
            src={kontaktBild}
            alt="Das Firmenschild von Cura Care in der Bahnhofstraße in Eitorf"
            className="contact-img"
            width="800"
            height="1067"
            loading="lazy"
          />
          <div className="contact-blob" aria-hidden="true" />
        </div>

        <div className="contact-form-card">
          <h2 className="contact-title">{title}</h2>
          <p className="contact-hint">{hint}</p>

          {done ? (
            <div className="contact-success" role="status">
              <div className="contact-success-icon" aria-hidden="true">✓</div>
              <p>
                Vielen Dank! Wir melden uns kurzfristig bei Ihnen.
              </p>
              <button type="button" className="contact-btn" onClick={() => setDone(false)}>
                Weitere Nachricht
              </button>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate>
              {/* Honeypot gegen Bots */}
              <input
                name="honey"
                value={form.honey}
                onChange={onChange}
                className="contact-hp"
                autoComplete="off"
                tabIndex={-1}
                aria-hidden="true"
              />

              <div className="contact-row">
                {field('name', 'Name', {type: 'text', autoComplete: 'name', placeholder: 'Vor- und Nachname'})}
                {field('email', 'E-Mail', {type: 'email', autoComplete: 'email', inputMode: 'email', placeholder: 'name@beispiel.de'})}
                {field('phone', 'Telefon', {type: 'tel', autoComplete: 'tel', inputMode: 'tel', placeholder: '02243 …'}, false)}
              </div>

              {field('reachability', 'Wann sind Sie am besten erreichbar?', {
                type: 'text',
                maxLength: 120,
                placeholder: 'z. B. vormittags, nach 17 Uhr oder lieber per E-Mail'
              }, false)}

              <div className="contact-field">
                <label htmlFor={`${id}-message`}>
                  Nachricht <span className="contact-optional">(optional)</span>
                </label>
                <textarea
                  id={`${id}-message`}
                  name="message"
                  placeholder="Worum geht es? Wer soll versorgt werden?"
                  rows={6}
                  value={form.message}
                  onChange={onChange}
                  maxLength={4000}
                />
              </div>

              <label className={`contact-consent ${errors.consent ? 'has-error' : ''}`}>
                <input
                  type="checkbox"
                  name="consent"
                  checked={form.consent}
                  onChange={onChange}
                  required
                />
                <span>
                  Ich habe die{' '}
                  <Link to="/datenschutz" target="_blank" rel="noreferrer">Datenschutzerklärung</Link>{' '}
                  zur Kenntnis genommen und bin mit der Verarbeitung meiner Angaben zur Bearbeitung
                  der Anfrage einverstanden.
                </span>
              </label>

              {errors.consent && <small className="contact-consent-error">{errors.consent}</small>}

              {submitError && (
                <p className="contact-error" role="alert">{submitError}</p>
              )}

              <button className="contact-btn" type="submit" disabled={loading}>
                {loading ? 'Wird gesendet …' : 'Nachricht absenden'}
              </button>

              <p className="contact-alt">
                Lieber anrufen? <a href={`tel:${company.phoneIntl}`}>{company.phone}</a> · {company.openingHours.label}
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
